import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateParam, transformDateToLocalDateString } from "helpers/common";
import { transformRoute } from "helpers";
import { VehicleTimelineGroup } from "services/api/notifications/types";
import { getVehicleTimelineAPI } from "services/api/notifications/api";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation/routes";
import { CalendarViewMode, getVisibleRange } from "ui/modules/notifications/calendarGrid";
import { CalendarToolbar } from "ui/modules/notifications/pages/components/CalendarToolbar";
import { DEFAULT_VIEW } from "ui/modules/notifications/pages/components/CalendarToolbar/consts";
import { MONTH_NAMES } from "./consts";
import VehicleTimeline from "./VehicleTimeline";

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);

  return next;
};

const VehicleTimelineView: React.FC = () => {
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarViewMode>(DEFAULT_VIEW);
  const [groups, setGroups] = useState<VehicleTimelineGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const range = useMemo(() => getVisibleRange(currentDate, view), [currentDate, view]);

  const fetchTimeline = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getVehicleTimelineAPI(
        formatDateParam(range.from)!,
        formatDateParam(range.to)!
      );
      setGroups(result);
    } catch {
      // greška ide kroz createApiCall
    }
    setIsLoading(false);
  }, [range.from, range.to]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const handlePrev = useCallback(() => {
    setCurrentDate((current) =>
      view === "week"
        ? addDays(current, -7)
        : new Date(current.getFullYear(), current.getMonth() - 1, 1)
    );
  }, [view]);

  const handleNext = useCallback(() => {
    setCurrentDate((current) =>
      view === "week"
        ? addDays(current, 7)
        : new Date(current.getFullYear(), current.getMonth() + 1, 1)
    );
  }, [view]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleVehicleClick = useCallback(
    (vehicleId: string) => {
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: vehicleId })
      );
    },
    [navigate]
  );

  const title = `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  const subtitle = `${transformDateToLocalDateString(range.from)} – ${transformDateToLocalDateString(range.to)}`;

  return (
    <div className="bg-white rounded-sm border">
      <CalendarToolbar
        view={view}
        title={title}
        subtitle={subtitle}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onViewChange={setView}
      />

      <VehicleTimeline
        groups={groups}
        isLoading={isLoading}
        from={range.from}
        to={range.to}
        onVehicleClick={handleVehicleClick}
      />
    </div>
  );
};

export default VehicleTimelineView;
